"use client";
import { useEffect, useMemo, useState } from "react";
import { filter as lodashFilter, orderBy } from "lodash";
import FestivalService, { Festival } from "@/app/_services/FestivalService";
import { toast } from "@/app/_utils/toast";

export function useFestivalsModel() {
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedFestival, setSelectedFestival] = useState<Festival | null>(null);
  const [openForm, setOpenForm] = useState(false);

  async function loadFestivals() {
    setIsLoading(true);
    try {
      const res = await FestivalService.getFestivalsAsync();
      setFestivals(orderBy(res, ["created_at"], ["desc"]));
    } catch (e) {
      toast.error("Failed to load festivals");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadFestivals();
  }, []);

  const filteredFestivals = useMemo(() => {
    if (!search.trim()) return festivals;
    const q = search.trim().toLowerCase();
    return lodashFilter(festivals, (f) => f.title?.toLowerCase().includes(q));
  }, [festivals, search]);

  function openCreateForm() {
    setSelectedFestival(null);
    setOpenForm(true);
  }

  function openEditForm(festival: Festival) {
    setSelectedFestival(festival);
    setOpenForm(true);
  }

  function closeForm() {
    setOpenForm(false);
  }

  async function handleDelete(festival: Festival) {
    try {
      await FestivalService.deleteFestivalAsync(festival.id);
      toast.success("Festival deleted");
      await loadFestivals();
    } catch (e) {
      toast.error("Failed to delete festival");
    }
  }

  async function handleTogglePublish(festival: Festival) {
    try {
      await FestivalService.publishFestivalAsync(festival.id, !festival.published);
      toast.success(festival.published ? "Festival unpublished" : "Festival published");
      await loadFestivals();
    } catch (e) {
      toast.error("Failed to update festival");
    }
  }

  return {
    filteredFestivals,
    isLoading,
    search,
    setSearch,
    selectedFestival,
    openForm,
    openCreateForm,
    openEditForm,
    closeForm,
    handleDelete,
    handleTogglePublish,
    refetch: loadFestivals,
  };
}
